"use client";

import React, { useState, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Navigation, ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Coverage() {
  const shouldReduceMotion = useReducedMotion();
  const [selectedRegion, setSelectedRegion] = useState<"socal" | "vegas">("socal");
  const socalTabRef = useRef<HTMLButtonElement>(null);
  const vegasTabRef = useRef<HTMLButtonElement>(null);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      if (selectedRegion === "socal") {
        setSelectedRegion("vegas");
        vegasTabRef.current?.focus();
      } else {
        setSelectedRegion("socal");
        socalTabRef.current?.focus();
      }
    }
  };
  const leftVariants = {
    hidden: {
      opacity: 0,
      x: shouldReduceMotion ? 0 : -20
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  const rightVariants = {
    hidden: {
      opacity: 0,
      scale: shouldReduceMotion ? 1 : 0.98
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.45,
        ease: "easeOut",
        delay: 0.1
      }
    }
  };
  return <section id="service-areas-snapshot" data-nav="light" className="relative bg-background py-16 md:py-24 lg:py-28 overflow-hidden border-b border-border">
      {/* Background structural grid decor */}
      <div className="absolute inset-0 pointer-events-none opacity-40 bg-[linear-gradient(to_right,hsl(214_32%_91%_/_0.5)_1px,transparent_1px),linear-gradient(to_bottom,hsl(214_32%_91%_/_0.5)_1px,transparent_1px)] bg-[size:4rem_4rem]" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          
          {/* Left Column: Information & Actions */}
          <motion.div variants={leftVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} className="lg:col-span-6 space-y-8">
            <div className="space-y-4">
              {/* Cold-Chain Pill Eyebrow */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent text-accent-foreground border border-border text-xs font-semibold uppercase tracking-wider">
                <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                SERVICE REACH
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default">
                Serving Southern California & the Las Vegas Area
              </h2>

              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                Serving{" "}
                <Link to="/LosAngelesRefrigeratedTrailerRentalPage" className="text-foreground underline underline-offset-2 hover:text-primary transition-colors">
                  Southern California
                </Link>{" "}
                and our listed{" "}
                <Link to="/LasVegasRefrigeratedTrailerRentalPage" className="text-foreground underline underline-offset-2 hover:text-primary transition-colors">
                  Las Vegas-area
                </Link>{" "}
                communities.
              </p>
            </div>

            {/* Action Group */}
            <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-muted font-semibold">
                <Link to="/ServiceAreasPage">
                  <span>View Service Areas</span>
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </Button>

              <Button asChild variant="default" size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-sm">
                <Link to="/ContactRequestAQuotePage">
                  <span>Request a Quote</span>
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: Information Card */}
          <motion.div variants={rightVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} className="lg:col-span-6">
            <div className="relative rounded-2xl bg-card border border-border p-6 sm:p-8 shadow-sm overflow-hidden">
              {/* Header inside graphic container */}
              <div className="flex items-center justify-between border-b border-border pb-4 mb-6">
                <div className="flex items-center gap-2.5">
                  <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center text-primary">
                    <Navigation className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-foreground tracking-wide font-default">
                      SERVICE REGION
                    </h3>
                  </div>
                </div>
              </div>

              {/* Styled Coverage Information Panel */}
              <div className="rounded-xl border border-border bg-muted/40 p-6 sm:p-8 space-y-5">
                <div role="tablist" aria-label="Select service region map" className="flex items-center gap-2">
                  <Button ref={socalTabRef} id="tab-southern-california" role="tab" variant="ghost" aria-selected={selectedRegion === "socal"} aria-controls="tabpanel-home-region-map" tabIndex={selectedRegion === "socal" ? 0 : -1} onClick={() => setSelectedRegion("socal")} onKeyDown={handleKeyDown} className={`rounded-full text-xs h-8 px-3.5 transition-colors ${selectedRegion === "socal" ? "bg-accent/60 text-accent-foreground font-semibold" : "bg-accent/30 text-muted-foreground hover:bg-accent/50"}`}>
                    Southern California
                  </Button>
                  <Button ref={vegasTabRef} id="tab-las-vegas-area" role="tab" variant="ghost" aria-selected={selectedRegion === "vegas"} aria-controls="tabpanel-home-region-map" tabIndex={selectedRegion === "vegas" ? 0 : -1} onClick={() => setSelectedRegion("vegas")} onKeyDown={handleKeyDown} className={`rounded-full text-xs h-8 px-3.5 transition-colors ${selectedRegion === "vegas" ? "bg-accent/60 text-accent-foreground font-semibold" : "bg-accent/30 text-muted-foreground hover:bg-accent/50"}`}>
                    Las Vegas Area
                  </Button>
                </div>

                <div role="tabpanel" id="tabpanel-home-region-map" aria-label={selectedRegion === "socal" ? "Fridge Fleet Southern California location" : "Fridge Fleet Las Vegas location"} className="space-y-3">
                  {selectedRegion === "socal" ? <iframe src="https://www.google.com/maps?q=13726+Saticoy+Street,+Panorama+City,+California&z=10&output=embed" title="Fridge Fleet Southern California location" loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen={true} className="w-full h-56 sm:h-64 rounded-md border border-border" /> : <iframe src="https://www.google.com/maps?q=7477+Ullom+Drive,+Las+Vegas,+Nevada&z=10&output=embed" title="Fridge Fleet Las Vegas location" loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen={true} className="w-full h-56 sm:h-64 rounded-md border border-border" />}

                  <div>
                    <Link to={selectedRegion === "socal" ? "https://maps.google.com/?q=13726+Saticoy+Street+Panorama+City+California" : "https://maps.google.com/?q=7477+Ullom+Drive+Las+Vegas+Nevada"} target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-primary underline underline-offset-2 hover:text-primary/80 transition-colors">
                      Open in Google Maps
                    </Link>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground leading-relaxed pt-1">
                  Map locations represent Fridge Fleet&apos;s service bases. Delivery coverage, trailer availability, and requirements are confirmed with each quote.
                </p>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}